import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import Dexie from "dexie";
import MetaMaskOnboarding from "@metamask/onboarding";
import { ethers } from "ethers";
import { importInto } from "dexie-export-import";
import { default as axios } from "axios";
import { Metaplex, walletAdapterIdentity } from "@metaplex-foundation/js";
import bs58 from "bs58";

import { getRecords } from "../Services/ApiClient";
import config from "../config.json";

const ethereum = window.ethereum;

async function fetchNFTsDBTable() {
    const {
        DatabaseName: dbname,
        TableName: tname,
        TablePKey: pkey,
        Version: v,
    } = config.NFTsDatabase;

    let db = new Dexie(dbname);
    db.version(v).stores({
        [tname]: pkey,
    });

    // Check local nfts table record size
    const localRecordSize = await db[tname].count();
    const REMOTE_DB_NFTS_SIZE =
        process.env.REACT_APP_NFTS_DATABASE_NFTS_SIZE ?? 0;
    if (localRecordSize !== +REMOTE_DB_NFTS_SIZE) {
        console.log("Importing from Remote");
        const REMOTE_DB_FILE_URL = process.env.REACT_APP_NFTS_DATABASE_FILE_URL;
        const { data: blob } = await axios.get(REMOTE_DB_FILE_URL, {
            responseType: "blob",
        });
        await importInto(db, blob, {
            clearTablesBeforeImport: true,
            progressCallback: ({ totalRows, completedRows }) => {
                console.log(
                    `Progress: ${completedRows} of ${totalRows} rows completed`
                );
            },
        });
        console.log("Import complete");
    }

    return db[tname];
}

export function useNFTsDBTable() {
    const {
        data: dbTable,
        error,
        isLoading,
    } = useSWRImmutable("nfts-db", fetchNFTsDBTable);

    return { dbTable, error, isLoading };
}

export function useUserNFTAddrs({ connection, wallet }) {
    const { data, error, isLoading, mutate, isValidating } = useSWR(
        ["nfts", wallet],
        async () => {
            const metaplex = Metaplex.make(connection).use(
                walletAdapterIdentity(wallet)
            );
            const rawNFTs = await metaplex
                .nfts()
                .findAllByOwner({ owner: metaplex.identity().publicKey });

            const nfts = rawNFTs.map((nft) => {
                return bs58.encode(nft.mintAddress.toBuffer());
            });
            console.log("All User's NFTs", nfts);
            return nfts;
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateOnMount: true,
        }
    );

    return {
        data,
        error,
        isLoading,
        mutate,
        isValidating,
    };
}

export function useReords({ jwt }) {
    const { data, error, isLoading, mutate, isValidating } = useSWR(
        ["records", jwt],
        async () => {
            const limitPerRequest = config.RecordsLimitPerRequest;
            let more = true;
            let total = 0;
            let lastRecordLabel = null;
            const records = [];
            while (more) {
                const sharding = await getRecords(
                    jwt,
                    lastRecordLabel,
                    limitPerRequest
                );
                if (sharding.total) {
                    total = sharding.total;
                }
                records.push(...sharding.records);
                if (records.length >= total) {
                    more = false;
                } else {
                    lastRecordLabel = records[records.length - 1].ItemLabel;
                }
            }
            return { records, total };
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateOnMount: true,
        }
    );

    return {
        rawRecords: data?.records ?? [],
        total: data?.total ?? 0,
        error,
        isLoading,
        mutate,
        isValidating,
    };
}

export function useMetamaskWalletAccount() {
    const { data, error, isLoading, mutate, isValidating } = useSWR(
        "ethereum-addr",
        async () => {
            if (!MetaMaskOnboarding.isMetaMaskInstalled()) {
                return [];
            }
            return ethereum.request({ method: "eth_accounts" });
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateOnMount: true,
        }
    );

    if (error) {
        console.error(error);
    }

    let account = "";
    if (data && data.length > 0) {
        console.log("Fetched Polygon Accts", data);
        account = ethers.getAddress(data[0]);
    }

    return { account, isLoading, refreshAccount: mutate, isValidating };
}

export function useMetamaskChainId() {
    const { data, error, isLoading, mutate, isValidating } = useSWR(
        "ethereum-chainId",
        async () => {
            if (!MetaMaskOnboarding.isMetaMaskInstalled()) {
                return "";
            }
            return ethereum.request({ method: "eth_chainId" });
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateOnMount: true,
        }
    );

    if (error) {
        console.error(error);
    }

    return {
        chainId: data ?? "",
        isLoading,
        refreshChainId: mutate,
        isValidating,
    };
}
